import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Ban, RotateCcw, Loader2, ShieldAlert, ShieldCheck, Crown } from 'lucide-react';
import { toast } from 'sonner';
import { apiClient } from '@/lib/api-client';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/providers/auth-provider';
import { AdminShell } from './admin-shell';

interface UserDetail {
  user: {
    userId: string; email: string; displayName: string | null;
    plan: string | null; authProvider: string | null;
    onboardingComplete: boolean | null;
    suspendedAt: string | null; suspendedReason: string | null;
    createdAt: string; updatedAt: string;
  };
  memberships: Array<{
    orgId: string; role: string; joinedAt: string;
    orgName: string | null; orgKind: string | null; archivedAt: string | null;
  }>;
  recentRuns: Array<{
    runId: string; agentType: string; status: string; createdAt: string;
    orgId: string; projectId: string;
  }>;
  isAdmin: boolean;
}

export function AdminUserDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const [confirming, setConfirming] = useState<'suspend' | 'unsuspend' | 'grant' | 'revoke' | null>(null);
  const [reason, setReason] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'user', userId],
    queryFn: () => apiClient.adminGetUser(userId!),
    enabled: !!userId,
  });
  const d = data?.data as UserDetail | undefined;

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['admin', 'user', userId] });
    qc.invalidateQueries({ queryKey: ['admin', 'users'] });
  };

  const suspend = useMutation({
    mutationFn: () => apiClient.adminSuspendUser(userId!, reason || undefined),
    onSuccess: () => { toast.success('User suspended'); setConfirming(null); setReason(''); refresh(); },
    onError: (err: Error) => toast.error(err.message),
  });
  const unsuspend = useMutation({
    mutationFn: () => apiClient.adminUnsuspendUser(userId!),
    onSuccess: () => { toast.success('User reinstated'); setConfirming(null); refresh(); },
    onError: (err: Error) => toast.error(err.message),
  });
  const grant = useMutation({
    mutationFn: () => apiClient.adminGrantAdmin(userId!),
    onSuccess: () => { toast.success('Admin granted'); setConfirming(null); refresh(); },
    onError: (err: Error) => toast.error(err.message),
  });
  const revoke = useMutation({
    mutationFn: () => apiClient.adminRevokeAdmin(userId!),
    onSuccess: () => { toast.success('Admin revoked'); setConfirming(null); refresh(); },
    onError: (err: Error) => toast.error(err.message),
  });
  const updatePlan = useMutation({
    mutationFn: (plan: 'free' | 'pro' | 'enterprise') => apiClient.adminUpdateUserPlan(userId!, plan),
    onSuccess: () => { toast.success('Plan updated'); refresh(); },
    onError: (err: Error) => toast.error(err.message),
  });

  if (isLoading || !d) {
    return <div className="p-8 text-center text-muted-foreground">Loading…</div>;
  }

  const u = d.user;
  const isSelf = userId === me?.userId;

  return (
    <AdminShell detail={u.displayName ?? u.email} subtitle="Inspect or take action on this user. Every action is recorded in the audit log.">

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2 flex-wrap">
            {u.displayName ?? u.email}
            {u.suspendedAt && (
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-500/15 text-red-600 inline-flex items-center gap-1">
                <Ban className="h-3 w-3" /> suspended
              </span>
            )}
            {d.isAdmin && (
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-saffron/20 text-saffron inline-flex items-center gap-1">
                <Crown className="h-3 w-3" /> admin
              </span>
            )}
          </h1>
          <p className="text-sm text-muted-foreground">{u.email} · joined {new Date(u.createdAt).toLocaleDateString()}</p>
          {u.suspendedReason && (
            <p className="text-xs text-red-600 dark:text-red-400 mt-1">Suspension reason: {u.suspendedReason}</p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {u.suspendedAt ? (
            <Button variant="outline" onClick={() => setConfirming('unsuspend')} disabled={unsuspend.isPending}>
              {unsuspend.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-2" />}
              Reinstate
            </Button>
          ) : (
            <Button variant="outline" onClick={() => setConfirming('suspend')} disabled={suspend.isPending || isSelf}
              title={isSelf ? "You can't suspend yourself" : 'Suspend user'}
              className="text-red-600 dark:text-red-400 hover:bg-red-500/10">
              <Ban className="h-4 w-4 mr-2" /> Suspend
            </Button>
          )}
          {d.isAdmin ? (
            <Button variant="outline" onClick={() => setConfirming('revoke')} disabled={revoke.isPending || isSelf}
              title={isSelf ? "Have another admin do it" : 'Revoke admin'}>
              <ShieldCheck className="h-4 w-4 mr-2" /> Revoke admin
            </Button>
          ) : (
            <Button variant="outline" onClick={() => setConfirming('grant')} disabled={grant.isPending}>
              <ShieldAlert className="h-4 w-4 mr-2" /> Grant admin
            </Button>
          )}
        </div>
      </div>

      {/* Confirmation panels */}
      {confirming === 'suspend' && (
        <Card className="p-5 border-red-500/30 bg-red-500/5">
          <p className="text-sm font-semibold mb-2 text-red-700 dark:text-red-300">Suspend this user?</p>
          <p className="text-xs text-muted-foreground mb-3">All API requests will be rejected until reinstated. They keep their data.</p>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="Reason (optional, recorded in audit log)"
            className="w-full text-sm border border-border rounded-md p-2 bg-background mb-3" rows={2} />
          <div className="flex gap-2">
            <Button onClick={() => suspend.mutate()} disabled={suspend.isPending} className="bg-red-600 hover:bg-red-700 text-white">
              {suspend.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Confirm suspend
            </Button>
            <Button variant="outline" onClick={() => { setConfirming(null); setReason(''); }}>Cancel</Button>
          </div>
        </Card>
      )}
      {confirming === 'unsuspend' && (
        <Card className="p-5">
          <p className="text-sm font-semibold mb-2">Reinstate this user?</p>
          <p className="text-xs text-muted-foreground mb-3">They regain access immediately.</p>
          <div className="flex gap-2">
            <Button onClick={() => unsuspend.mutate()} disabled={unsuspend.isPending}>Confirm reinstate</Button>
            <Button variant="outline" onClick={() => setConfirming(null)}>Cancel</Button>
          </div>
        </Card>
      )}
      {confirming === 'grant' && (
        <Card className="p-5 border-saffron/30 bg-saffron/5">
          <p className="text-sm font-semibold mb-2">Grant admin to {u.email}?</p>
          <p className="text-xs text-muted-foreground mb-3">They will see the Admin nav item on next sign-in (or token refresh) and can take cross-tenant actions.</p>
          <div className="flex gap-2">
            <Button onClick={() => grant.mutate()} disabled={grant.isPending}>Confirm grant admin</Button>
            <Button variant="outline" onClick={() => setConfirming(null)}>Cancel</Button>
          </div>
        </Card>
      )}
      {confirming === 'revoke' && (
        <Card className="p-5">
          <p className="text-sm font-semibold mb-2">Revoke admin from {u.email}?</p>
          <p className="text-xs text-muted-foreground mb-3">They lose admin access on next token refresh (~1 hour).</p>
          <div className="flex gap-2">
            <Button onClick={() => revoke.mutate()} disabled={revoke.isPending}>Confirm revoke</Button>
            <Button variant="outline" onClick={() => setConfirming(null)}>Cancel</Button>
          </div>
        </Card>
      )}

      {/* Profile */}
      <Card className="p-5 grid grid-cols-2 md:grid-cols-4 gap-4">
        <Field label="User ID"><span className="font-mono text-xs">{u.userId}</span></Field>
        <Field label="Email">{u.email}</Field>
        <Field label="Auth provider">{u.authProvider ?? '—'}</Field>
        <Field label="Plan">
          <select
            value={u.plan ?? 'free'}
            onChange={(e) => updatePlan.mutate(e.target.value as 'free' | 'pro' | 'enterprise')}
            className="text-sm border border-border rounded px-2 py-1 bg-background"
          >
            <option value="free">free</option>
            <option value="pro">pro</option>
            <option value="enterprise">enterprise</option>
          </select>
        </Field>
        <Field label="Onboarding">{u.onboardingComplete ? 'complete' : 'in progress'}</Field>
        <Field label="Joined">{new Date(u.createdAt).toLocaleString()}</Field>
        <Field label="Last updated">{new Date(u.updatedAt).toLocaleString()}</Field>
      </Card>

      {/* Memberships */}
      <Card className="p-0 overflow-hidden">
        <div className="px-5 py-3 border-b border-border">
          <p className="text-sm font-medium uppercase tracking-wide">Memberships ({d.memberships.length})</p>
        </div>
        <div className="divide-y divide-border">
          {d.memberships.map((m) => (
            <Link key={m.orgId} to={`/admin/orgs/${m.orgId}`}
              className="px-5 py-3 flex items-center justify-between gap-3 hover:bg-muted/40">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate flex items-center gap-2">
                  {m.orgName ?? m.orgId}
                  {m.archivedAt && <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-muted text-muted-foreground">archived</span>}
                </p>
                <p className="text-xs text-muted-foreground capitalize">{m.orgKind} org · joined {new Date(m.joinedAt).toLocaleDateString()}</p>
              </div>
              <span className="text-xs text-muted-foreground capitalize shrink-0">{m.role}</span>
            </Link>
          ))}
          {d.memberships.length === 0 && (
            <p className="px-5 py-6 text-sm text-muted-foreground text-center">No memberships.</p>
          )}
        </div>
      </Card>

      {/* Recent runs */}
      <Card className="p-0 overflow-hidden">
        <div className="px-5 py-3 border-b border-border">
          <p className="text-sm font-medium uppercase tracking-wide">Recent agent runs ({d.recentRuns.length})</p>
        </div>
        <div className="divide-y divide-border">
          {d.recentRuns.map((r) => (
            <div key={r.runId} className="px-5 py-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium">{r.agentType}</p>
                <p className="text-xs text-muted-foreground truncate font-mono">{r.runId}</p>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground shrink-0">
                <span className="capitalize">{r.status}</span>
                <span>{new Date(r.createdAt).toLocaleString()}</span>
              </div>
            </div>
          ))}
          {d.recentRuns.length === 0 && (
            <p className="px-5 py-6 text-sm text-muted-foreground text-center">No agent runs.</p>
          )}
        </div>
      </Card>
    </AdminShell>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">{label}</p>
      <div className="text-sm">{children}</div>
    </div>
  );
}
